import {
  CalendarDate,
  getLocalTimeZone,
  parseDate
} from '@internationalized/date';
import { maybe, Maybe, Present } from '@passionware/monads';
import moment from 'moment-timezone';
import { getSystemTimezone } from 'v5/core/system-timezone';

const isDateOnly = (dateStr: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
};

export type GetLocalTimezone = () => string;

export const createDateConvert = (
  getLocalTimezone: GetLocalTimezone,
  getInternationalizedLocalTimeZone: GetLocalTimezone
) => {
  const api = {
    translate: {
      fromSystemSpaceToLocalSpace: (date: Date): Date => {
        // Convert to system timezone while preserving wall clock time
        return moment(date)
          .tz(getSystemTimezone(), false)
          .tz(getLocalTimezone(), true)
          .toDate();
      },
      fromLocalSpaceToSystemSpace: (date: Date): Date => {
        // Convert to system timezone while preserving wall clock time
        return moment(date)
          .tz(getLocalTimezone(), false)
          .tz(getSystemTimezone(), true)
          .toDate();
      }
    },
    dateOnly: {
      parse: {
        /**
         * Converts a date-only string (YYYY-MM-DD) to a local Date object
         *
         * @param dateString - A string in YYYY-MM-DD format
         * @returns {Date} A JavaScript Date object at midnight in the local timezone
         *
         * Example:
         * "2024-03-20" -> Date representing March 20, 2024 00:00:00 in local timezone
         */
        toLocalDate: (dateString: string): Date => {
          return moment.tz(dateString, getLocalTimezone()).toDate();
        }
      },
      format: {
        /**
         * Converts a local Date object to a date-only string
         *
         * @param date - A JavaScript Date object
         * @returns {string} Date string in YYYY-MM-DD format
         *
         * Example:
         * Date(2024-03-20 15:30:00) -> "2024-03-20"
         */
        fromLocalDate: (date: Date): string => {
          return moment(date).tz(getLocalTimezone()).format('YYYY-MM-DD');
        }
      }
    },
    mixed: {
      parse: {
        /**
         * Converts a string to a local Date object, handling both date-only and datetime strings
         *
         * @param dateString - A string in either YYYY-MM-DD format or a full UTC datetime string
         * @returns {Date} A JavaScript Date object in the local timezone
         *
         * For date-only strings (YYYY-MM-DD):
         * - Interprets the date in the local timezone
         * - Returns a Date at midnight local time on that date
         *
         * For UTC datetime strings:
         * - Converts the UTC time to local timezone
         * - e.g. "2024-03-20T15:30:00Z" becomes 15:30 local time on March 20
         */
        toLocalDate: (dateString: string): Date => {
          if (isDateOnly(dateString)) {
            return moment.tz(dateString, getLocalTimezone()).toDate();
          } else {
            return moment.utc(dateString).utcOffset(0, true).toDate();
          }
        },
        /**
         * Converts a string to a local Date object while preserving the wall clock time from system timezone
         *
         * @param dateString - A string in either YYYY-MM-DD format or a full UTC datetime string
         * @returns {Date} A JavaScript Date object in the local timezone
         *
         * For date-only strings (YYYY-MM-DD):
         * - Interprets the date in the local timezone
         * - Returns a Date at midnight local time on that date
         *
         * For UTC datetime strings:
         * - First converts to system timezone
         * - Then preserves the wall clock time when converting to local timezone
         * - e.g. If it's 15:30 in system time, it will be 15:30 in local time
         */
        preserveSystemClock: (dateString: string): Date => {
          if (isDateOnly(dateString)) {
            return moment.tz(dateString, getLocalTimezone()).toDate();
          } else {
            const utcDate = moment.utc(dateString).utcOffset(0, true).toDate();
            return dateConvert.translate.fromSystemSpaceToLocalSpace(utcDate);
          }
        }
      },
      format: {
        /**
         * Converts a local Date object to either a date-only string or UTC timestamp
         *
         * @param date - A JavaScript Date object
         * @param options - Configuration options
         * @param options.dateOnly - If true, returns YYYY-MM-DD format, otherwise returns UTC timestamp
         * @returns {string} Either a date-only string or UTC timestamp
         *
         * Example with dateOnly: true:
         * Date(2024-03-20 15:30:00) -> "2024-03-20"
         *
         * Example with dateOnly: false:
         * Date(2024-03-20 15:30:00) -> "2024-03-20T15:30:00Z"
         */
        fromLocalDate: (date: Date, options: { dateOnly: boolean }): string => {
          if (options.dateOnly) {
            return moment(date).tz(getLocalTimezone()).format('YYYY-MM-DD');
          } else {
            return moment(date).tz(getLocalTimezone()).utc().format();
          }
        },
        /**
         * Converts a local Date object to either a date-only string or UTC timestamp,
         * while preserving the wall clock time from system timezone
         *
         * @param date - A JavaScript Date object
         * @param options - Configuration options
         * @param options.dateOnly - If true, returns YYYY-MM-DD format, otherwise returns UTC timestamp
         * @returns {string} Either a date-only string or UTC timestamp
         *
         * For date-only output:
         * - Returns the date in YYYY-MM-DD format
         *
         * For UTC timestamp output:
         * - First converts to system timezone
         * - Then converts to UTC while preserving the wall clock time
         * - e.g. If it's 15:30 in system time, the UTC timestamp will represent 15:30 UTC
         */
        preserveSystemClock: (
          date: Date,
          options: { dateOnly: boolean }
        ): string => {
          if (options.dateOnly) {
            return moment(date).tz(getLocalTimezone()).format('YYYY-MM-DD');
          } else {
            const systemDate =
              dateConvert.translate.fromLocalSpaceToSystemSpace(date);
            return moment(systemDate).tz(getSystemTimezone()).utc().format();
          }
        }
      }
    },
    /**
     * Experimental: Utility wrappers for @internationalized/date primitives (CalendarDate, Time, etc.)
     * This toolset provides convenience methods for common conversions and creation of date/time objects.
     */
    calendarDate: {
      /**
       * Converts a date value (DateString, Date, or null) to CalendarDate
       *
       * @param value - A string in YYYY-MM-DD format, a Date object, or null
       * @returns CalendarDate if value is provided, null if value is null
       *
       * @example
       * calendarDate.from("2024-03-20") -> CalendarDate(2024, 3, 20)
       * calendarDate.from(new Date(2024, 2, 20)) -> CalendarDate(2024, 3, 20)
       * calendarDate.from(null) -> null
       * calendarDate.from("invalid") -> throws Error
       */
      unsafeFrom<T extends Maybe<string | Date>>(
        value: T
      ): T extends Present<unknown> ? CalendarDate : null {
        if (maybe.isAbsent(value)) {
          return null as T extends Present<unknown> ? CalendarDate : null;
        }

        try {
          if (typeof value === 'string') {
            // DateString in YYYY-MM-DD format
            return parseDate(value);
          } else if (value instanceof Date) {
            if (isNaN(value.getTime())) {
              throw new Error('Invalid date');
            }
            // Date object - convert to CalendarDate using constructor
            // This matches the pattern from DateInput.tsx
            return new CalendarDate(
              value.getFullYear(),
              value.getMonth() + 1,
              value.getDate()
            );
          }
        } catch {
          // Invalid date format
          throw new Error('Invalid date format');
        }

        throw new Error('Invalid date format');
      },
      /**
       * Converts a string or Date to a CalendarDate, returning null if the conversion fails
       *
       * @param value - A string in YYYY-MM-DD format or a Date object
       * @returns CalendarDate if value is provided, null if value is null
       *
       * @example
       * calendarDate.unsafeFrom("2024-03-20") -> CalendarDate(2024, 3, 20)
       * calendarDate.unsafeFrom(new Date(2024, 2, 20)) -> CalendarDate(2024, 3, 20)
       * calendarDate.unsafeFrom("invalid") -> null
       * calendarDate.unsafeFrom(null) -> null
       */
      from: (value: string | Date | null): CalendarDate | null => {
        try {
          return api.calendarDate.unsafeFrom(value);
        } catch {
          return null;
        }
      },
      /**
       * Converts a CalendarDate to a Date object
       *
       * @param calendarDate - A CalendarDate object to convert
       * @returns Date object representing the calendar date at midnight in local timezone, or null if input is null
       *
       * @example
       * calendarDate.toDate(CalendarDate(2024, 3, 20)) -> Date(2024-03-20T00:00:00)
       * calendarDate.toDate(null) -> null
       */
      toLocalDate<T extends Maybe<CalendarDate>>(
        calendarDate: T
      ): T extends Present<unknown> ? Date : null {
        if (maybe.isAbsent(calendarDate)) {
          return null as T extends Present<unknown> ? Date : null;
        }
        // Convert CalendarDate to Date at midnight in local timezone
        return calendarDate.toDate(getInternationalizedLocalTimeZone());
      }
    }
  };
  return api;
};

export const dateConvert = createDateConvert(
  () => moment.tz.guess(),
  getLocalTimeZone
);

/**
 * @deprecated Use dateConvert
 * @param date
 */
export function localDateToStringDateStringTimestamp(date: Date) {
  return moment(date).local().format('YYYY-MM-DD');
}
/**
 * @deprecated Use dateConvert
 * @param dateString
 */
export function dateStringTimestampToLocalDate(dateString: string) {
  return moment.utc(dateString).utcOffset(0, true).toDate();
}

export function stringToJsDate(date: string) {
  return new Date(date);
}
